import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  DashboardType,
  LeoAnalysisType,
  LeoButtonPopoverType,
  LeoComparisonPeriod,
  LeoConfirmLeavePromptAction,
  LeoDashboardAnalysisScope,
  LeoDashboardAnalysisType,
  LeoStatus,
} from '@vision/ui/enums';
import {
  Flow,
  LeoAnalysis,
  LeoAnalysisProgress,
  LeoAnalysisSuggestion,
  LeoComparisonRange,
} from '@vision/ui/interfaces';
import { FlowsService, LeoService } from '@vision/ui/services';

export interface LeoStateType {
  analysis: LeoAnalysis;
  analyzeHistory: LeoAnalysis[];
  analysisType: LeoAnalysisType;
  buttonPopover: LeoButtonPopoverType;
  confirmLeavePrompt: {
    action: LeoConfirmLeavePromptAction;
    onConfirm: VoidFunction;
    onCancel: VoidFunction;
  };
  credits: number;
  currentAnalyzeProgress: LeoAnalysisProgress;
  leoStatus: LeoStatus;
  flows: Flow[];
  historyType: LeoAnalysisType;
  isHistoryVisible: boolean;
  historyLoading: boolean;
}

// The dashboard-history endpoint does not return the dashboard type, so recover
// it from the cards: only ATA analyses carry an ATA node_type on their content.
const detectAtaDashboardType = (comments: LeoAnalysis['comments']): DashboardType | undefined =>
  comments?.some((comment) => Boolean(comment.content?.node_type)) ? DashboardType.ATA_DASHBOARD : undefined;

const initialState: LeoStateType = {
  analysis: null,
  analyzeHistory: [],
  analysisType: null,
  buttonPopover: LeoButtonPopoverType.NONE,
  confirmLeavePrompt: null,
  credits: null,
  currentAnalyzeProgress: null,
  leoStatus: LeoStatus.CLOSED,
  flows: [],
  historyType: null,
  isHistoryVisible: true,
  historyLoading: false,
};

export const LeoState = createSlice({
  name: 'LeoState',
  initialState,
  reducers: {
    setCurrentAnalyzeProgress: (
      state,
      action: PayloadAction<{
        entityId?: string;
        entityName?: string;
        dashboardType?: DashboardType;
      }>,
    ) => {
      if (action.payload) {
        state.analysis = null;
        state.currentAnalyzeProgress = {
          ...state.currentAnalyzeProgress,
          ...action.payload,
          answers: {},
        };
      } else {
        state.currentAnalyzeProgress = null;
      }
    },
    setAnswers: (state, action: PayloadAction<Record<string, string>>) => {
      if (state.currentAnalyzeProgress) {
        state.currentAnalyzeProgress.answers = { ...state.currentAnalyzeProgress.answers, ...action.payload };
      }
    },
    clearAnswers: (state) => {
      if (state.currentAnalyzeProgress) {
        state.currentAnalyzeProgress.answers = {};
      }
    },
    setAnalysisFromHistory: (state, action: PayloadAction<LeoAnalysis>) => {
      state.analysis = { ...action.payload, is_new: false, from_history: true };
      state.currentAnalyzeProgress = null;
      state.confirmLeavePrompt = null;
    },
    toggleHistoryVisibility: (state) => {
      state.isHistoryVisible = !state.isHistoryVisible;
    },
    setPopover: (state, action: PayloadAction<LeoButtonPopoverType>) => {
      state.buttonPopover = action.payload;
    },
    openLeoDrawer: (state) => {
      state.leoStatus = LeoStatus.OPENED;
      state.buttonPopover = LeoButtonPopoverType.NONE;

      if (!state.currentAnalyzeProgress) {
        state.isHistoryVisible = true;
      }
    },
    minimizeLeoDrawer: (state) => {
      state.leoStatus = LeoStatus.MINIMIZED;
      state.buttonPopover = LeoButtonPopoverType.MINIMIZED;
    },
    closeLeoDrawer: (state) => {
      state.analysis = null;
      state.analysisType = null;
      state.buttonPopover = LeoButtonPopoverType.NONE;
      state.confirmLeavePrompt = null;
      state.currentAnalyzeProgress = null;
      state.leoStatus = LeoStatus.CLOSED;
      state.isHistoryVisible = false;
    },
    openLeoSuggestions: (state, action: PayloadAction<string>) => {
      const analysisId = action.payload;
      const analysis = analysisId ? state.analyzeHistory.find((a) => a.id === analysisId) : null;

      if (analysis) {
        state.analysis = analysis;
      }

      state.leoStatus = LeoStatus.SUGGESTIONS;
    },
    setAnalysis: (state, action: PayloadAction<LeoAnalysis>) => {
      state.analysis = action.payload;
    },
    setAnalysisType: (state, action: PayloadAction<LeoAnalysisType>) => {
      state.analysisType = action.payload;
    },
    setConfirmLeavePrompt: (
      state,
      action: PayloadAction<{
        action: LeoConfirmLeavePromptAction;
        onCancel: VoidFunction;
        onConfirm: VoidFunction;
      }>,
    ) => {
      state.confirmLeavePrompt = action.payload;
    },
    resetConfirmLeavePrompt: (state) => {
      state.confirmLeavePrompt = null;
    },
    setHistoryType: (state, action: PayloadAction<LeoAnalysisType>) => {
      state.historyType = action.payload;
    },
    setHistoryLoading: (state, action: PayloadAction<boolean>) => {
      state.historyLoading = action.payload;
    },
    setDashboardAnalysisScope: (state, action: PayloadAction<LeoDashboardAnalysisScope>) => {
      if (state.currentAnalyzeProgress) {
        state.currentAnalyzeProgress.dashboardAnalysisScope = action.payload;
      }
    },
    setDashboardAnalysisType: (state, action: PayloadAction<LeoDashboardAnalysisType>) => {
      if (state.currentAnalyzeProgress) {
        state.currentAnalyzeProgress.dashboardAnalysisType = action.payload;
      }
    },
    setComparisonPeriod: (state, action: PayloadAction<LeoComparisonPeriod>) => {
      if (state.currentAnalyzeProgress) {
        state.currentAnalyzeProgress.comparisonPeriod = action.payload;
      }
    },
    setComparisonRange: (state, action: PayloadAction<LeoComparisonRange | undefined>) => {
      if (state.currentAnalyzeProgress) {
        state.currentAnalyzeProgress.comparisonRange = action.payload;
      }
    },
    setDashboardWidgets: (state, action: PayloadAction<string[]>) => {
      if (state.currentAnalyzeProgress) {
        state.currentAnalyzeProgress.dashboardWidgets = action.payload;
      }
    },
    setAnalysisSuggestions: (state, action: PayloadAction<LeoAnalysisSuggestion[]>) => {
      if (state.analysis) {
        state.analysis.comments = action.payload;
      }
    },
    appendAnalysisComments: (state, action: PayloadAction<LeoAnalysisSuggestion[]>) => {
      if (!state.analysis) {
        return;
      }

      const existingIds = new Set(state.analysis.comments.map((comment) => comment.id));
      const additions = action.payload.filter((comment) => !existingIds.has(comment.id));
      state.analysis.comments = [...state.analysis.comments, ...additions];

      const historyIndex = state.analyzeHistory.findIndex((entry) => entry.id === state.analysis.id);
      if (historyIndex >= 0) {
        state.analyzeHistory[historyIndex] = state.analysis;
      }
    },
    setAnalysisIsNewFlag: (state, action: PayloadAction<boolean>) => {
      if (state.analysis) {
        state.analysis.is_new = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(LeoService.endpoints.analyzeFlow.matchFulfilled, (state, action) => {
      const analysis: LeoAnalysis = {
        ...action.payload.analysis,
        entity_id: state.currentAnalyzeProgress.entityId,
        entity_name: state.currentAnalyzeProgress.entityName,
        entity_type: state.analysisType,
        is_new: true,
      };

      state.analysis = analysis;
      state.currentAnalyzeProgress = null;

      if (state.historyType === state.analysisType) {
        state.analyzeHistory.push(analysis);
      }
    });

    builder.addMatcher(LeoService.endpoints.dashboardAnalyzeHistory.matchPending, (state) => {
      state.analyzeHistory = [];
      state.historyLoading = true;
    });

    builder.addMatcher(LeoService.endpoints.flowAnalyzeHistory.matchPending, (state) => {
      state.analyzeHistory = [];
      state.historyLoading = true;
    });
    builder.addMatcher(LeoService.endpoints.analyzeDashboard.matchFulfilled, (state, action) => {
      const analysis: LeoAnalysis = {
        ...action.payload.analysis,
        entity_id: state.currentAnalyzeProgress.entityId,
        entity_name: state.currentAnalyzeProgress.entityName,
        entity_type: state.analysisType,
        dashboardType: state.currentAnalyzeProgress.dashboardType,
        is_new: true,
      };

      state.analysis = analysis;
      state.currentAnalyzeProgress = null;

      if (state.historyType === state.analysisType) {
        state.analyzeHistory.push(analysis);
      }
    });
    builder.addMatcher(LeoService.endpoints.quota.matchFulfilled, (state, action) => {
      state.credits = action.payload.quota;
    });
    builder.addMatcher(LeoService.endpoints.dashboardAnalyzeHistory.matchFulfilled, (state, action) => {
      state.analyzeHistory = action.payload
        .map((dashboardHistory) =>
          dashboardHistory.leo_histories.map((history) => ({
            ...history,
            entity_id: dashboardHistory.id,
            entity_name: dashboardHistory.name,
            entity_type: LeoAnalysisType.DASHBOARD,
            dashboardType: detectAtaDashboardType(history.comments),
          })),
        )
        .flat();

      state.historyLoading = false;
    });
    builder.addMatcher(LeoService.endpoints.flowAnalyzeHistory.matchFulfilled, (state, action) => {
      state.analyzeHistory = action.payload
        .map((flowHistory) =>
          flowHistory.leo_histories.map((history) => ({
            ...history,
            entity_id: flowHistory.id,
            entity_name: flowHistory.name,
            entity_type: LeoAnalysisType.FLOW,
          })),
        )
        .flat();

      state.historyLoading = false;
    });
    builder.addMatcher(LeoService.endpoints.favorite.matchFulfilled, (state, action) => {
      if (state.analysis && state.analysis.id === action.payload.id) {
        state.analysis.is_favourite = true;
      }

      if (state.analyzeHistory.length > 0) {
        const analysis = state.analyzeHistory.find((analysis: LeoAnalysis) => analysis.id === action.payload.id);
        analysis.is_favourite = true;
      }
    });
    builder.addMatcher(LeoService.endpoints.unfavorite.matchFulfilled, (state, action) => {
      if (state.analysis && state.analysis.id === action.payload.id) {
        state.analysis.is_favourite = false;
      }

      if (state.analyzeHistory.length > 0) {
        const analysis = state.analyzeHistory.find((analysis: LeoAnalysis) => analysis.id === action.payload.id);
        analysis.is_favourite = false;
      }
    });
    builder.addMatcher(LeoService.endpoints.setSuggestionReaction.matchFulfilled, (state, action) => {
      const { suggestionId } = action.meta.arg.originalArgs;

      const suggestion = state.analysis.comments.find((comment: LeoAnalysisSuggestion) => comment.id === suggestionId);

      if (suggestion) {
        suggestion.reactions = [...suggestion.reactions, action.payload];
      }

      if (state.analyzeHistory.length > 0) {
        state.analyzeHistory = state.analyzeHistory.filter(
          (historyAnalysis: LeoAnalysis) => historyAnalysis.id !== state.analysis.id,
        );

        state.analyzeHistory.push(state.analysis);
      }
    });
    builder.addMatcher(LeoService.endpoints.destroySuggestionReaction.matchFulfilled, (state, action) => {
      const { reactionId } = action.meta.arg.originalArgs;

      const suggestion = state.analysis.comments.find((comment: LeoAnalysisSuggestion) =>
        comment.reactions.find((r) => r.id === reactionId),
      );

      if (suggestion) {
        suggestion.reactions = suggestion.reactions.filter((r) => r.id !== reactionId);
      }

      if (state.analyzeHistory.length > 0) {
        state.analyzeHistory = state.analyzeHistory.filter(
          (historyAnalysis: LeoAnalysis) => historyAnalysis.id !== state.analysis.id,
        );

        state.analyzeHistory.push(state.analysis);
      }
    });
    builder.addMatcher(FlowsService.endpoints.getFlows.matchFulfilled, (state, action) => {
      state.flows = action.payload;
    });
  },
});

export const {
  appendAnalysisComments,
  clearAnswers,
  closeLeoDrawer,
  minimizeLeoDrawer,
  openLeoDrawer,
  openLeoSuggestions,
  resetConfirmLeavePrompt,
  setAnalysisFromHistory,
  setAnalysisIsNewFlag,
  setAnalysisSuggestions,
  setAnalysisType,
  setAnswers,
  setConfirmLeavePrompt,
  setCurrentAnalyzeProgress,
  setComparisonPeriod,
  setComparisonRange,
  setDashboardAnalysisScope,
  setDashboardAnalysisType,
  setDashboardWidgets,
  setHistoryLoading,
  setHistoryType,
  setPopover,
  toggleHistoryVisibility,
} = LeoState.actions;
